import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import CTASection from "@/components/sections/CTASection";
import FAQAccordion from "@/components/sections/FAQAccordion";
import { Reveal } from "@/components/motion/Reveal";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import { faqs } from "@/content/faqs";
import { CheckCircle2 } from "lucide-react";

const steps = [
  {
    step: "01",
    title: "Free Growth Assessment",
    desc: "Understand the business, target services, current lead sources, digital presence, follow-up process, capacity and growth priorities.",
  },
  {
    step: "02",
    title: "Recommended Growth Plan",
    desc: "Identify the biggest gaps and recommend the most appropriate package, priorities, service area and initial roadmap.",
  },
  {
    step: "03",
    title: "Onboarding and Foundation",
    desc: "Collect approved assets, establish access, configure tracking, confirm consent requirements, optimise the foundations and document responsibilities.",
  },
  {
    step: "04",
    title: "Build and Launch",
    desc: "Create the selected campaigns, content, landing pages, pipeline and workflows. Obtain client approval before public launch where required.",
  },
  {
    step: "05",
    title: "Manage and Respond",
    desc: "Operate the recurring activities and response services included in the subscription.",
  },
  {
    step: "06",
    title: "Measure and Improve",
    desc: "Review commercial indicators, identify bottlenecks and improve the system over time.",
  },
];

const clarity = [
  { label: "Green Circle Group manages", value: "The recurring growth activity agreed in your subscription—visibility, campaigns, response and reporting." },
  { label: "You approve", value: "Advertisements, public content, offers and major commercial decisions before they go live where required." },
  { label: "We need from you", value: "Access to relevant accounts, approved assets and the information required to represent your business accurately." },
  { label: "How leads reach you", value: "Enquiries are captured, and—depending on the plan—responded to, qualified and booked, then handed over clearly." },
  { label: "How performance is reviewed", value: "Through the commercial indicators and review cadence confirmed in your plan and proposal." },
];

export default function HowItWorks() {
  return (
    <>
      <SEO
        title="Growth on Cruise Control | How Green Circle Group Works"
        description="See how Green Circle Group assesses, builds, manages and improves the digital growth system around an Australian trade business."
        path="/how-it-works"
      />
      <PageHero
        eyebrow="How It Works"
        heading="Growth on cruise control. You remain in the driver's seat."
        copy="Green Circle Group manages the recurring growth activity agreed in your subscription. You retain control of the business, brand, customer relationships and commercial decisions."
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "How It Works", to: "/how-it-works" },
        ]}
      >
        <Btn to="/contact">Book a Free Growth Assessment</Btn>
      </PageHero>

      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <Reveal
                key={s.step}
                delay={(i % 3) * 0.06}
                className="rounded-[1.75rem] bg-white border border-border-neutral p-8 relative overflow-hidden"
              >
                <span className="absolute -right-3 -top-4 text-8xl font-extrabold text-soft select-none">
                  {s.step}
                </span>
                <div className="relative">
                  <span className="text-sm font-bold tracking-[0.2em] text-mint">
                    STEP {s.step}
                  </span>
                  <h2 className="mt-3 text-xl font-extrabold text-forest">
                    {s.title}
                  </h2>
                  <p className="mt-3 text-mid-neutral leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement clarity */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Eyebrow>Engagement Clarity</Eyebrow>
          <SectionHeading className="mt-5">
            Clear on who does what—from the start.
          </SectionHeading>
          <div className="mt-12 space-y-4">
            {clarity.map((c, i) => (
              <Reveal
                key={c.label}
                delay={i * 0.05}
                className="flex flex-col md:flex-row md:items-start gap-3 md:gap-8 rounded-2xl border border-border-neutral p-6"
              >
                <div className="md:w-64 shrink-0 flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-mint shrink-0" />
                  <span className="font-extrabold text-forest">{c.label}</span>
                </div>
                <p className="text-mid-neutral leading-relaxed">{c.value}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-sm text-mid-neutral">
            Specific response times, service levels and reporting frequency are
            confirmed in your plan and proposal.
          </p>
        </div>
      </section>

      {/* Full FAQ */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <SectionHeading className="mt-5">Frequently asked questions</SectionHeading>
          </div>
          <div className="mt-12">
            <FAQAccordion items={faqs} emitSchema />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
